import { computed } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { useAuthStore } from '@/stores/auth';
import { get } from '@vueuse/core';

export function useData() {
  const getAuthStore = () => useAuthStore();
  // const { logout } = useAuth();

  const loginProviders = [
    {
      id: 'google',
      name: 'Google',
      icon: 'icon-[logos--google-icon]',
      iconDark: 'icon-[mdi--google]',
    },
    {
      id: 'microsoft',
      name: 'Microsoft',
      icon: 'icon-[logos--microsoft-icon]',
      iconDark: 'icon-[mdi--microsoft]',
    },
    {
      id: 'github',
      name: 'GitHub',
      icon: 'icon-[logos--github-icon]',
      iconDark: 'icon-[mdi--github]',
    },
  ];

  const assetTypes = [
    { name: 'Image', code: 'IMAGE' },
    { name: 'Audio', code: 'AUDIO' },
    { name: 'Video', code: 'VIDEO' },
  ];

  const features = [
    {
      image: '/features/superactions.png',
      title: 'Superpowered Actions',
      icon: 'icon-[mdi--sparkles]',
      description:
        'AI-augmented actions ready to integrate with your agentic frameworks, transform your media, and create new content.',
    },
    {
      image: '/features/medialibrary.png',
      title: 'Convenient Media Libraries',
      icon: 'icon-[mdi--library]',
      description:
        'Easily manage all your media assets - audio, images, video, and search through them intelligently.',
    },
    {
      image: '/features/cdn.png',
      title: 'Powerful CDN',
      icon: 'icon-[gis--network]',
      description:
        'Distribute your media assets globally with our powerful CDN, and deliver them to your users at blazing speeds.',
    },
  ];

  const menuItems = computed(() => [
    {
      label: 'Dashboard',
      icon: 'icon-[material-symbols--dashboard]',
      route: {
        name: 'Dashboard',
        path: '/dashboard',
      },
      permission: 'VIEW_DASHBOARD',
    },
    {
      label: 'ACTIONS',
      items: [
        {
          label: 'Discover actions',
          icon: 'icon-[mdi--sparkles]',
          route: {
            name: 'DiscoverActions',
            path: '/discover',
          },
          permission: 'VIEW_ACTION_JOBS',
        },
        {
          label: 'Jobs history',
          icon: 'icon-[mdi--history]',
          route: {
            name: 'ActionJobsList',
            path: '/jobs',
          },
          permission: 'VIEW_ACTION_JOBS',
        },
      ],
    },
    // {
    //   label: 'CDN',
    //   items: [
    //     {
    //       label: 'Distributions',
    //       icon: 'icon-[gis--network]',
    //       route: {
    //         name: 'Distributions',
    //         path: '/distributions',
    //       },
    //       permission: 'VIEW_DISTRIBUTIONS',
    //     },
    //     {
    //       label: 'Certificates',
    //       icon: 'icon-[mdi--certificate]',
    //       route: {
    //         name: 'Certificates',
    //         path: '/certificates',
    //       },
    //       permission: 'VIEW_CERTIFICATES',
    //     },
    //   ],
    // },
    // {
    //   label: 'MEDIA',
    //   items: [
    //     {
    //       label: 'Libraries',
    //       icon: 'icon-[mdi--library]',
    //       route: {
    //         name: 'Libraries',
    //         path: '/libraries',
    //       },
    //       permission: 'VIEW_LIBRARIES',
    //     },
    //   ],
    // },
    {
      label: 'IAM',
      items: [
        {
          label: 'API keys',
          icon: 'icon-[bitcoin-icons--two-keys-filled]',
          route: {
            name: 'Keys',
            path: '/keys',
          },
          permission: 'VIEW_KEYS',
        },
        {
          label: 'Team',
          icon: 'icon-[ic--baseline-people]',
          route: {
            name: 'Team',
            path: '/team',
          },
          permission: 'VIEW_TEAM_MEMBERS',
        },
      ],
    },
    {
      label: 'Billing',
      items: [
        {
          label: 'Invoices',
          icon: 'icon-[mdi--invoice-list]',
          route: {
            name: 'Invoices',
            path: '/invoices',
          },
          permission: 'VIEW_INVOICES',
        },
        {
          label: 'Billing details',
          icon: 'icon-[solar--dollar-bold]',
          route: {
            name: 'BillingDetails',
            path: '/billing-details',
          },
          permission: 'VIEW_BILLING_INFO',
        },
      ],
    },
  ]);

  const tagSeverity = {
    // Media
    IMPORT_INITIATED: 'info',
    IMPORT_PROCESSING: 'info',
    IMPORT_COMPLETE: 'success',
    IMPORT_FAILED: 'danger',
    UPLOAD_INITIATED: 'info',
    UPLOAD_PROCESSING: 'info',
    UPLOAD_COMPLETE: 'success',
    UPLOAD_FAILED: 'danger',
    // CDN
    Deployed: 'success',
    Completed: 'success',
    InProgress: 'info',
    Failed: 'danger',
    EXPIRED: 'warn',
    FAILED: 'danger',
    INACTIVE: 'warn',
    // Certificates
    ISSUED: 'success',
    PENDING_VALIDATION: 'info',
    REVOKED: 'danger',
    VALIDATION_TIMED_OUT: 'danger',
    SUCCESS: 'success',
    // Invoices
    PAID: 'success',
    NO_CHARGE: 'success',
    CURRENT: 'info',
    UPCOMING: 'info',
    DUE: 'warn',
    OVERDUE: 'danger',
    // Action Jobs
    QUEUED: 'warn',
    IN_PROGRESS: 'info',
    COMPLETED: 'success',
    FAILED: 'danger',
  };
  const severityColors = {
    info: 'blue',
    success: 'green',
    danger: 'red',
    warn: 'yellow',
  };
  const getSeverityTextColor = (status) =>
    severityColors[tagSeverity[status]]
      ? `text-${severityColors[tagSeverity[status]]}-500`
      : '';

  const httpErrorCodes = [
    { code: 400, name: 'Bad request' },
    { code: 403, name: 'Forbidden' },
    { code: 404, name: 'Not found' },
    { code: 405, name: 'Method not allowed' },
    { code: 414, name: 'Request-URI too long' },
    { code: 416, name: 'Range not satisfiable' },
    { code: 500, name: 'Internal server error' },
    { code: 501, name: 'Not implemented' },
    { code: 502, name: 'Bad gateway' },
    { code: 503, name: 'Service unavailable' },
    { code: 504, name: 'Gateway timeout' },
  ];

  const httpResponseCodes = [{ code: 200, name: 'OK' }, ...httpErrorCodes];

  const memberRoles = {
    ADMIN: 'Admin',
    DEVELOPER: 'Developer',
    READONLY: 'Read-only',
    FINANCE: 'Finance',
  };

  // List of the supported Action Collections
  const actionCollections = [
    {
      id: 'featured',
      title: 'Featured',
      icon: 'icon-[ph--star-four-bold]',
    },
    {
      id: 'popular',
      title: 'Popular',
      icon: 'icon-[akar-icons--trophy]',
    },
    {
      id: 'trending',
      title: 'Trending',
      icon: 'icon-[gridicons--trending]',
    },
    {
      id: 'highly_rated',
      title: 'Highly rated',
      icon: 'icon-[tabler--award]',
    },
    {
      id: 'new',
      title: 'New',
      icon: 'icon-[heroicons-outline--lightning-bolt]',
    },
  ];

  return {
    loginProviders,
    assetTypes,
    features,
    menuItems,
    tagSeverity,
    severityColors,
    getSeverityTextColor,
    httpErrorCodes,
    httpResponseCodes,
    memberRoles,
    actionCollections,
  };
}
